// src/features/auth/services/tokenService.js
import { jwtDecode } from 'jwt-decode';

export class TokenService {
  getToken() {
    return localStorage.getItem('access_token');
  }

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  setTokens(accessToken, refreshToken) {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  isTokenExpired(token = null) {
    const tokenToCheck = token || this.getToken();
    if (!tokenToCheck) return true;

    try {
      const decoded = this.getTokenPayload(tokenToCheck);
      if (!decoded) return true;
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  getTokenPayload(token = null) {
    const tokenToCheck = token || this.getToken();
    if (!tokenToCheck) return null;

    // Permission checks run this dozens of times per render (every nav item,
    // button, …) and the token carries the full scope list, so decoding it each
    // time cost hundreds of ms per navigation. Cache the last decoded token.
    if (tokenToCheck === this._cachedToken) return this._cachedPayload;

    let payload;
    try {
      payload = jwtDecode(tokenToCheck);
    } catch (error) {
      payload = null;
    }
    this._cachedToken = tokenToCheck;
    this._cachedPayload = payload;
    this._cachedScopeSet = new Set(payload?.scope || []);
    return payload;
  }

  getUserId() {
    const payload = this.getTokenPayload();
    return payload?.user_id || null;
  }

  getDepartmentId() {
    const payload = this.getTokenPayload();
    return payload?.department_id || null;
  }

  getScopes() {
    const payload = this.getTokenPayload();
    return payload?.scope || [];
  }

  hasScope(requiredScope) {
    if (!this.getTokenPayload()) return false;
    const scopes = this._cachedScopeSet;

    // Check for wildcard permissions
    if (scopes.has('*')) return true;
    if (scopes.has('*:*')) return true;

    // Check for resource wildcard (e.g., 'module:*')
    const [resource] = requiredScope.split(':');
    if (scopes.has(`${resource}:*`)) return true;

    // Check for exact match
    return scopes.has(requiredScope);
  }

  hasAnyScope(requiredScopes) {
    return requiredScopes.some(scope => this.hasScope(scope));
  }

  getTokenExpirationTime() {
    const payload = this.getTokenPayload();
    return payload?.exp ? new Date(payload.exp * 1000) : null;
  }

  getTimeUntilExpiration() {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return 0;

    return Math.max(0, expirationTime.getTime() - Date.now());
  }
}

export const tokenService = new TokenService();